import React from "react";
import "./tenure-education.css";

const resources = [
  {
    href: "/resources/campus-deal-guide.html#rent",
    index: "01",
    title: "What sits behind the rent?",
    description:
      "How cost plans, inclusions and assumptions connect to a proposed campus rent.",
  },
  {
    href: "/resources/campus-deal-guide.html#affordability",
    index: "02",
    title: "Will the campus work as enrolments grow?",
    description:
      "Testing the opening years, the enrolment ramp and the period of greatest cash pressure.",
  },
  {
    href: "/resources/campus-deal-guide.html#proposal",
    index: "03",
    title: "What to ask before agreeing terms",
    description:
      "Questions on scope, milestones, incentives, security and review mechanisms.",
  },
];

const connected = [
  {
    label: "Undertow",
    title: "Site intelligence",
    body: "Explore the site context and the questions a potential campus raises.",
  },
  {
    label: "TENURE",
    title: "Campus financial modelling",
    body: "Test the proposed development, lease and operating assumptions.",
  },
  {
    label: "Stratosfyre",
    title: "Property advisory",
    body: "Bring site strategy, feasibility and negotiation into the decision.",
  },
];

const faqs = [
  {
    question: "Does TENURE replace my broker or adviser?",
    answer:
      "No. TENURE supports shared understanding between you and your advisers. Project-specific costs, valuations and terms still need scrutiny from your quantity surveyor, valuer, lawyer and financier.",
  },
  {
    question: "Can we benchmark the developer’s proposal?",
    answer:
      "Construction benchmarks are available by school specification. Transaction comparisons and quarterly evidence updates can be scoped with the team, including source dates, coverage and adjustments. Benchmarks inform the negotiation; they do not replace project-specific cost or valuation advice.",
  },
  {
    question: "How does TENURE use demographics?",
    answer:
      "Catchment age, schooling and household-income data provide context for enrolment and fees. You set the operating assumptions; the model does not treat population data as guaranteed demand.",
  },
  {
    question: "What can I take to my advisers and board?",
    answer:
      "Export formula-driven Microsoft Excel workbooks and structured PDF reports covering the proposal, assumptions, operating and development economics, sensitivities and evidence context. Your advisers can examine the workings and your board can follow the decision.",
  },
  {
    question: "Which markets does TENURE cover?",
    answer:
      "Australia is the active market. New Zealand and Singapore are coming soon. Discuss your location and project with the team to establish the available model and benchmark coverage.",
  },
  {
    question: "Is it only for education?",
    answer:
      "Education is the starting point. If your business depends on a long-term property commitment, we can discuss whether the current model fits your operating structure.",
  },
];

export function TenureEducationSections(): React.JSX.Element {
  return (
    <>
      <section id="resources" className="tenure-education tenure-reveal">
        <p className="tenure-eyebrow">Resources</p>
        <h2>Before you commit to a campus.</h2>
        <p className="tenure-resource-introduction">
          Practical notes for education operators and their advisers.
        </p>
        <div className="tenure-resource-grid">
          {resources.map((item) => (
            <a
              key={item.href}
              className="tenure-resource-card"
              href={item.href}
            >
              <span className="tenure-card-index" aria-hidden="true">
                {item.index}
              </span>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              <span className="tenure-card-arrow" aria-hidden="true">
                →
              </span>
            </a>
          ))}
        </div>
      </section>

      <section id="connected" className="tenure-education tenure-reveal">
        <p className="tenure-eyebrow">Connected capabilities</p>
        <h2>From the site search to the long-term decision.</h2>
        <div className="tenure-connected-grid">
          {connected.map((item) => (
            <article key={item.label} className="tenure-connected-card">
              <p className="tenure-connected-label">{item.label}</p>
              <h3>{item.title}</h3>
              <p className="tenure-connected-body">{item.body}</p>
            </article>
          ))}
        </div>
        <p className="tenure-footnote">
          Discuss these complementary services in your walkthrough.
        </p>
      </section>

      <section id="faq" className="tenure-education tenure-reveal">
        <p className="tenure-eyebrow">FAQ</p>
        <h2>Common questions.</h2>
        <div className="tenure-faq-list">
          {faqs.map((faq) => (
            <details key={faq.question} className="tenure-faq-item">
              <summary>{faq.question}</summary>
              <p>{faq.answer}</p>
            </details>
          ))}
        </div>
      </section>
    </>
  );
}
